import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Profile as ProfileType } from '../types';
import { Pencil, Save, X, Zap } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';

export default function Profile() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const setShowUpgradeModal = useStore((state) => state.setShowUpgradeModal);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    level: '',
    team: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const docRef = doc(db, 'profiles', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const profileData = {
            id: docSnap.id,
            ...docSnap.data(),
          } as ProfileType;
          setProfile(profileData);
          setFormData({
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            age: profileData.age.toString(),
            level: profileData.level,
            team: profileData.team,
          });
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const updatedProfile = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        age: parseInt(formData.age, 10),
        level: formData.level,
        team: formData.team,
        updatedAt: new Date(),
      };

      const docRef = doc(db, 'profiles', user.uid);
      await updateDoc(docRef, updatedProfile);

      // Update local state
      setProfile((prev) => (prev ? { ...prev, ...updatedProfile } : null));
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };

  const handleUpgrade = async () => {
    if (!user) return;
    setShowUpgradeModal(true);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4'></div>
          <p className='text-gray-600'>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='max-w-2xl mx-auto px-4 py-8'>
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded'>
          {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className='max-w-2xl mx-auto px-4 py-8'>
        <div className='bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded'>
          {t('messages.profileNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-2xl mx-auto px-4 py-8'>
      <div className='flex justify-between items-center mb-6 md:mb-8'>
        <h1 className='text-2xl md:text-3xl font-bold text-primary-600 italic'>
          {t('profile.title')}
        </h1>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className='text-primary-500 hover:text-primary-600 flex items-center'
            title='Edit profile'
          >
            <Pencil className='h-5 w-5' />
          </button>
        )}
      </div>

      <div className='glass-card p-6'>
        {isEditing ? (
          <form onSubmit={handleSubmit} className='space-y-4 md:space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('profile.firstName')}
                </label>
                <input
                  type='text'
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('profile.lastName')}
                </label>
                <input
                  type='text'
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('profile.age')}
                </label>
                <input
                  type='number'
                  value={formData.age}
                  onChange={(e) =>
                    setFormData({ ...formData, age: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500'
                  min='1'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('profile.level')}
                </label>
                <select
                  value={formData.level}
                  onChange={(e) =>
                    setFormData({ ...formData, level: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500'
                  required
                >
                  <option value=''>{t('auth.selectLevel')}</option>
                  <option value='Recreational'>{t('auth.recreational')}</option>
                  <option value='Junior'>{t('auth.junior')}</option>
                  <option value='High School'>{t('auth.highSchool')}</option>
                  <option value='College'>{t('auth.college')}</option>
                  <option value='Semi-Professional'>{t('auth.semiProfessional')}</option>
                  <option value='Professional'>{t('auth.professional')}</option>
                </select>
              </div>
              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-700'>
                  {t('profile.team')}
                </label>
                <input
                  type='text'
                  value={formData.team}
                  onChange={(e) =>
                    setFormData({ ...formData, team: e.target.value })
                  }
                  className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500'
                  required
                />
              </div>
            </div>

            <div className='flex justify-end space-x-3 pt-4'>
              <button
                type='button'
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    age: profile.age.toString(),
                    level: profile.level,
                    team: profile.team,
                  });
                }}
                className='px-4 py-2 text-gray-700 hover:text-gray-900 flex items-center'
              >
                <X className='h-4 w-4 mr-2' />
                {t('common.cancel')}
              </button>
              <button
                type='submit'
                className='bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 flex items-center'
              >
                <Save className='h-4 w-4 mr-2' />
                {t('common.save')}
              </button>
            </div>
          </form>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('common.name')}</h3>
              <p className='mt-1 text-base md:text-lg text-gray-900'>
                {profile.firstName} {profile.lastName}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('profile.age')}</h3>
              <p className='mt-1 text-base md:text-lg text-gray-900'>
                {profile.age}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('profile.level')}</h3>
              <p className='mt-1 text-base md:text-lg text-gray-900'>
                {profile.level}
              </p>
            </div>
            <div>
              <h3 className='text-sm font-medium text-gray-500'>{t('profile.team')}</h3>
              <p className='mt-1 text-base md:text-lg text-gray-900'>
                {profile.team}
              </p>
            </div>
            <div className='md:col-span-2'>
              <h3 className='text-sm font-medium text-gray-500'>{t('auth.email')}</h3>
              <p className='mt-1 text-base md:text-lg text-gray-900'>
                {user?.email}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Section - Only show if user hasn't paid */}
      {profile && profile.paymentStatus !== 'paid' && (
        <div className='mt-8 bg-gradient-to-r from-primary-50 to-indigo-50 rounded-lg p-6 shadow-sm border border-primary-100'>
          <div>
            <h2 className='text-xl font-semibold text-gray-900'>
              {t('nav.upgradeToPro')}
            </h2>
            <p className='mt-2 text-gray-600'>
              {t('profile.upgradeDescription')}
            </p>
            <ul className='mt-4 space-y-3'>
              <li className='flex items-center text-gray-700'>
                <svg
                  className='h-5 w-5 text-primary-500 mr-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                {t('profile.unlimitedGameAnalysis')}
              </li>
              <li className='flex items-center text-gray-700'>
                <svg
                  className='h-5 w-5 text-primary-500 mr-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                {t('profile.advancedAIFeatures')}
              </li>
              <li className='flex items-center text-gray-700'>
                <svg
                  className='h-5 w-5 text-primary-500 mr-3'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                {t('profile.prioritySupport')}
              </li>
            </ul>
            <div className='mt-6 flex justify-center'>
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className='group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-full hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg overflow-hidden'
              >
                <span className='absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-shimmer' />
                <Zap className='h-5 w-5 mr-2' />
                <span>{t('profile.upgradeNow')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}